// Lib Imports
import React, { useEffect, useState, useCallback, useMemo, useContext, useReducer } from 'react';
import { Formik } from 'formik';
import { Box, DropButton, Keyboard, ResponsiveContext, TextInput, Button } from 'grommet';
import { Sort, Search, FormClose } from 'grommet-icons';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import styled, { createGlobalStyle, withTheme } from 'styled-components';
import PropTypes from 'prop-types';
// import { format } from 'date-fns';

import useQuery from 'granite-admin/utils/useQuery';
import { ConfigContext } from 'granite-admin/core/components/ConfigProvider';
import CommonAPI from 'granite-admin/common/gateways/common-api';
import EventEmitter from 'granite-admin/utils/event-emitter';
import graniteAPIClient from 'granite-admin/utils/granite-client';
import { getGrandEmitter } from 'granite-admin/utils/grandEmitter';
import DropContent, { renderLabel } from './components/DropContent';
const FilterIcon = styled(Sort)`
  width: 18px;
  height: 18px;
`;
const ItemDropButton = styled(DropButton)`
  padding: 0 0.25rem;
  border: 1px solid ${props => props.theme.global.colors['border']};
  border-radius: 4px;
  max-width: ${props => props.theme?.graniteTable?.filterDropProps ? 'auto' : '17rem'};
`;
const StyledInput = styled(TextInput)`
  border-radius: ${props => props.theme.global.searchBarBorderRadius};
`;
const GlobalStyle = createGlobalStyle`
  body > div[class^="css-"] {
    z-index: 20;
    & .select__menu-list {
      color: ${props => props.theme.global.colors['text'].light};
      & > div {
        line-height: 24px;
      }
    }
  }
`;
const DropStyle = createGlobalStyle`
  .table-filters-drop {
    width : fit-content !important;
  }
`;
const Filters = ({
  config: defaultConfig,
  canSearch,
  searchWidth,
  searchPlaceholder,
  queryKey,
  withoutQuery,
  initialState,
  onChange,
  showAppliedFilters,
  replaceSearch,
  replaceSearchWith,
  validations,
  setLocalFilter,
  theme,
  componentBesidesSearchBar
}) => {
  const {
    query,
    pathname
  } = useQuery();
  const history = useHistory();
  const size = useContext(ResponsiveContext);
  const eventEmitter = useMemo(() => new EventEmitter(), []);
  const grandEmitter = useMemo(() => getGrandEmitter(), []);
  const customConfig = useContext(ConfigContext);
  const {
    FilterComponent
  } = customConfig?.filterComponent;
  const configReducer = (configArray, action) => {
    if (action.type === 'replace') {
      return configArray.map(config => {
        if (config.name === action.payload.name) {
          config = action.payload.data;
        }
        return config;
      });
    }
  };
  const [config, dispatchConfig] = useReducer(configReducer, defaultConfig || []);
  useEffect(() => {
    const subscription = eventEmitter.getObservable().subscribe(event => {
      switch (event.type) {
        case 'UPDATE_FILTER_DATA':
          dispatchConfig({
            type: 'replace',
            payload: {
              name: event.data.name,
              data: event.data.payload
            }
          });
          break;
        default:
          console.log('HERE', event);
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [eventEmitter]);
  useEffect(() => {
    const subscription = grandEmitter.getObservable().subscribe(event => {
      switch (event.type) {
        case 'CLEAR_TABLE_SEARCH':
          setSearchFilter('');
          break;
        default:
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [grandEmitter]);

  // if options are updated in one of the objects in the config array
  // from the page where filters are mounted
  // update those config objects

  useEffect(() => {
    defaultConfig.forEach(newC => {
      config.forEach(oldC => {
        if (oldC.name === newC.name) {
          let data;
          if (oldC.options?.length === 0 && newC.options?.length > 0) data = newC;
          if (data) eventEmitter.emit('UPDATE_FILTER_DATA', {
            name: data.name,
            payload: data
          });
        }
      });
    });
  }, [defaultConfig, eventEmitter, config]);
  const filterParams = useMemo(() => {
    return initialState;
  }, [initialState]);
  const [searchFilter, setSearchFilter] = useState(filterParams?.search ? filterParams?.search : query[queryKey] ? JSON.parse(query[queryKey]).search : '');
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilters, updateAppliedFilters] = useState(() => {
    const obj = {};
    config.forEach(i => i.value !== undefined && i.value !== null && (obj[i.name] = i.value));
    return Object.keys(filterParams).length ? filterParams : obj;
  });
  useEffect(() => updateAppliedFilters({
    ...filterParams
  }), [filterParams]);
  const navigateToParams = useCallback(params => {
    let updatedParams = {
      ...appliedFilters,
      ...params
    };
    updatedParams = Object.entries(updatedParams) && Object.entries(updatedParams).length ? Object.fromEntries(Object.entries(updatedParams).filter(([_, v]) => v != null)) : updatedParams;
    const newObject = Object.keys(updatedParams).reduce((acc, key) => {
      const _acc = acc;
      if (updatedParams[key] !== undefined) _acc[key] = updatedParams[key];
      return _acc;
    }, {});
    updateAppliedFilters({
      ...newObject
    });
    if (withoutQuery) {
      setLocalFilter({
        ...newObject
      });
      onChange({
        ...appliedFilters,
        ...params,
        page: 1,
        ...(initialState?.page_size && {
          page_size: initialState.page_size
        }),
        ...(initialState?.ordering && {
          ordering: initialState.ordering
        })
      });
    } else {
      const filters = {
        ...updatedParams,
        page: 1,
        ...(initialState?.page_size && {
          page_size: initialState.page_size
        }),
        ...(initialState?.ordering && {
          ordering: initialState.ordering
        })
      };
      let pathObj = {
        pathname,
        search: queryString.stringify({
          ...query,
          [queryKey]: JSON.stringify(filters) // a seprate key is set coz we only want this component to re-render if filters are changed
        })
      };

      onChange({
        ...appliedFilters,
        ...params,
        page: 1,
        ...(initialState?.page_size && {
          page_size: initialState.page_size
        }),
        ...(initialState?.ordering && {
          ordering: initialState.ordering
        })
      });
      history.push(pathObj);
    }
  }, [appliedFilters, history, pathname, query, queryKey, withoutQuery, onChange, initialState, setLocalFilter]);
  const initialValue = useMemo(() => {
    const obj = {};
    config.forEach(i => obj[i.name] = appliedFilters[i.name]);
    return obj;
  }, [appliedFilters, config]);
  const handleOnEnter = e => {
    const value = e.target.value;
    if (value && value !== appliedFilters.search) navigateToParams({
      search: value
    });
  };
  useEffect(() => {
    if (!searchFilter && appliedFilters.search) navigateToParams({
      search: ''
    });
  }, [searchFilter, appliedFilters.search, navigateToParams]);

  // Add fetched options to the state of this filter
  const updateAsyncSelectOptions = (name, options) => {
    let updatedConfig = config.find(x => x.name === name && x.type === 'async-select');
    updatedConfig = {
      ...updatedConfig,
      options
    };
    eventEmitter.emit('UPDATE_FILTER_DATA', {
      name: updatedConfig.name,
      payload: updatedConfig
    });
  };

  // Call the fetch function provided in the async-select filter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAsyncSelectOptions = async (name, fetch, queryParams) => {
    let params = {
      ...{
        page_size: 1000,
        page: 1
      },
      ...queryParams
    };
    let data = await fetch(params);
    updateAsyncSelectOptions(name, data?.list);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchFromDataSource = async (name, url, params) => {
    try {
      const {
        data
      } = await graniteAPIClient('').get(url, {
        params
      });
      updateAsyncSelectOptions(name, data?.results);
    } catch (e) {
      console.log('err in fetch'.e?.errors);
    }
  };

  // Find the applied filters with type async-select and fetch their options
  useEffect(() => {
    let appliedFilterKeys = Object.keys(appliedFilters);
    let appliedAsyncSelectFilters = [];
    config.forEach(c => {
      if (appliedFilterKeys.includes(c.name) && c.type === 'async-select') appliedAsyncSelectFilters.push(c);
    });
    appliedAsyncSelectFilters.forEach(f => {
      if (f.fetch && !f.options) fetchAsyncSelectOptions(f.name, f.fetch, f.queryParams);else if (f.dataSource && !f.options) fetchFromDataSource(f.name, f.dataSource, f.queryParams);
    });
  }, [appliedFilters, config, fetchAsyncSelectOptions, fetchFromDataSource]);

  // Get config with name and entityName
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchConfigField = async (name, entityName) => {
    try {
      const field = await CommonAPI.getConfigByName(name, {
        entity_name: entityName
      });
      if (!field.disableSort && Array.isArray(field.options)) field.options.sort();
      field.itemType = field.type;
      delete field.type;
      updateConfigField(field);
    } catch (error) {
      console.log('error at fetchConfigField', error);
    }
  };

  // Add fetched field date to the state of this filter
  const updateConfigField = data => {
    let updatedConfig = config?.find(x => x.label === data?.name && x.type === 'config-master');
    delete updatedConfig.value;
    updatedConfig = {
      ...updatedConfig,
      ...data,
      name: updatedConfig.name
    };
    eventEmitter.emit('UPDATE_FILTER_DATA', {
      name: updatedConfig.name,
      payload: updatedConfig
    });
  };

  // Find the applied filters with type config-master and fetch their options
  useEffect(() => {
    let appliedFilterKeys = Object.keys(appliedFilters);
    let appliedConfigMasterFilters = [];
    config.forEach(c => {
      if (appliedFilterKeys.includes(c.name) && c.type === 'config-master') appliedConfigMasterFilters.push(c);
    });
    appliedConfigMasterFilters.forEach(f => {
      if (f.name && !f.options) fetchConfigField(f.label, f.extra_data?.entity_name);
    });
  }, [appliedFilters, config, fetchConfigField]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    gap: "small",
    direction: size !== 'small' ? 'row-reverse' : 'column',
    justify: canSearch || replaceSearch ? 'between' : 'end',
    flex: {
      shrink: 0
    },
    margin: {
      bottom: 'small'
    },
    align: "center"
  }, canSearch ? /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: "small",
    justify: "between",
    align: "center",
    wrap: true
  }, componentBesidesSearchBar, /*#__PURE__*/React.createElement(Keyboard, {
    onEnter: handleOnEnter
  }, /*#__PURE__*/React.createElement(Box, {
    width: searchWidth || theme?.graniteTable?.searchBarWidth || 'large',
    flex: {
      shrink: 0
    }
  }, /*#__PURE__*/React.createElement(StyledInput, {
    type: "search",
    icon: /*#__PURE__*/React.createElement(Search, {
      size: "small",
      color: "brand"
    }),
    value: searchFilter,
    onChange: e => setSearchFilter(e.target.value),
    placeholder: searchPlaceholder
  })))) : replaceSearch ? /*#__PURE__*/React.createElement(React.Fragment, null, replaceSearchWith) : null, /*#__PURE__*/React.createElement(Formik, {
    enableReinitialize: true,
    initialValues: initialValue,
    onSubmit: navigateToParams
  }, ({
    values,
    handleChange,
    handleSubmit,
    setFieldError,
    errors,
    setErrors
  }) => FilterComponent ? /*#__PURE__*/React.createElement(FilterComponent, {
    theme: theme,
    navigateToParams: navigateToParams,
    config: config,
    setShowFilter: setShowFilter,
    eventEmitter: eventEmitter,
    validations: validations,
    showAppliedFilters: showAppliedFilters,
    appliedFilters: appliedFilters,
    showFilter: showFilter,
    FilterIcon: FilterIcon,
    DropStyle: DropStyle,
    DropContent: DropContent,
    renderLabel: renderLabel
  }) : /*#__PURE__*/React.createElement(Box, null, config.length ? /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit,
    autoComplete: "off"
  }, /*#__PURE__*/React.createElement("input", {
    autoComplete: "false",
    name: "hidden",
    type: "text",
    style: {
      display: 'none'
    }
  }), /*#__PURE__*/React.createElement(Box, {
    gap: "small",
    direction: "row"
  }, /*#__PURE__*/React.createElement(DropButton, {
    alignSelf: "start",
    focusIndicator: false,
    plain: true,
    icon: /*#__PURE__*/React.createElement(FilterIcon, {
      color: Object.keys(values).find(v => values[v] !== undefined) ? 'brand' : theme?.graniteTable?.filterIconColor ?? '#c6c6c6'
    }),
    label: "Filter",
    dropAlign: {
      top: 'bottom',
      left: 'right'
    },
    dropProps: {
      elevation: 'small',
      margin: 'small'
    },
    dropContent: /*#__PURE__*/React.createElement(DropContent, {
      onClose: () => {
        setShowFilter(false);
        setErrors({});
      },
      config: config,
      values: values,
      handleChange: handleChange,
      navigateToParams: navigateToParams,
      buttonLabel: "Apply",
      heading: "Choose filters",
      eventEmitter: eventEmitter,
      errors: errors,
      validations: validations,
      setFieldError: setFieldError,
      setErrors: setErrors
    }),
    open: showFilter === 'filter',
    onOpen: () => setShowFilter('filter'),
    onClose: () => {
      setShowFilter(false);
      setErrors({});
    }
  }), showAppliedFilters && /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: "small",
    wrap: true
  }, React.Children.toArray(config.map(item => Object.prototype.hasOwnProperty.call(appliedFilters, item.name) && /*#__PURE__*/React.createElement(Box, {
    style: {
      marginBottom: '10px'
    }
  }, theme?.graniteTable?.filterDropProps && /*#__PURE__*/React.createElement(DropStyle, null), /*#__PURE__*/React.createElement(ItemDropButton, {
    alignSelf: "start",
    focusIndicator: false,
    plain: true,
    label: renderLabel(item, values, true),
    icon: /*#__PURE__*/React.createElement(Button, {
      as: "span",
      plain: true,
      icon: /*#__PURE__*/React.createElement(FormClose, {
        size: "small"
      }),
      onClick: () => navigateToParams({
        [item.name]: undefined
      })
    }),
    reverse: true,
    dropAlign: {
      top: 'bottom',
      left: 'right'
    },
    dropProps: {
      elevation: 'small',
      margin: 'small',
      className: 'table-filters-drop '
    },
    dropContent: /*#__PURE__*/React.createElement(DropContent, {
      onClose: () => {
        setShowFilter(false);
        setErrors({});
      },
      config: config,
      values: values,
      item: item,
      heading: "Update Filter",
      buttonLabel: "Update",
      showBack: false,
      navigateToParams: navigateToParams,
      eventEmitter: eventEmitter,
      errors: errors,
      setErrors: setErrors,
      validations: validations,
      setFieldError: setFieldError
    }),
    open: showFilter === item,
    onOpen: () => setShowFilter(item),
    onClose: () => {
      setShowFilter(false);
      setErrors({});
    }
  }))))))) : null))), /*#__PURE__*/React.createElement(GlobalStyle, null));
};
Filters.defaultProps = {
  queryKey: 'q',
  searchPlaceholder: 'Search...',
  config: [],
  canSearch: true,
  searchWidth: '',
  replaceSearch: false,
  replaceSearchWith: /*#__PURE__*/React.createElement(React.Fragment, null),
  showAppliedFilters: true,
  initialState: {},
  setLocalFilter: () => {},
  componentBesidesSearchBar: /*#__PURE__*/React.createElement(React.Fragment, null)
};
Filters.propTypes = {
  queryKey: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  config: PropTypes.array.isRequired,
  canSearch: PropTypes.bool,
  searchWidth: PropTypes.string,
  replaceSearch: PropTypes.bool,
  replaceSearchWith: PropTypes.node,
  withoutQuery: PropTypes.bool,
  initialState: PropTypes.object,
  onChange: PropTypes.func,
  setLocalFilter: PropTypes.func,
  showAppliedFilters: PropTypes.bool,
  validations: PropTypes.any,
  theme: PropTypes.object,
  componentBesidesSearchBar: PropTypes.node
};
export default withTheme(Filters);